import React, { useCallback, useRef } from 'react';
import { EnterpriseFormModal, FieldHint, type FormModalMode } from 'components/enterprise-shell';
import { SyutSelect, type SelectOption } from 'components/ui';
import { chargeTaxMappingService } from 'api/services/chargeTaxMappingService';
import { taxMasterService } from 'api/services/taxMasterService';
import { roomCategoryService } from 'api/services/roomCategoryService';
import { mealPlanService } from 'api/services/mealPlanService';
import { addonService } from 'api/services/addonService';
import {
  TAX_TREATMENT_OPTIONS,
  isItemChargeType,
  type ChargeTaxMapping,
  type ChargeTaxTreatment,
  type ChargeType,
} from 'api/types/chargeTaxMapping.types';
import { formatDay, newRuleConsequence, stopRuleConsequence, todayIso } from './taxRules';

/**
 * ============================================================================
 * TAX RULE FORM MODAL (Charge Tax Mappings)
 * ============================================================================
 * Rules are append-only, so the three wrapper modes mean:
 *   create → Add a tax rule (the server ends an open rule for the same charge
 *            and item on the new start date)
 *   copy   → Replace: a new rule prefilled from a current one, from a later date
 *   edit   → Stop: end a current rule on a date with no replacement; only the
 *            end date and remarks are editable
 * The "What will happen" box states the outcome from the entered values and is
 * repeated as the save confirmation. The server owns every rule; its refusal
 * text is shown as the error.
 * ============================================================================
 */

export interface ChargeTaxMappingForm {
  chargeType: ChargeType | '';
  itemId: string;
  itemName: string;
  rateCode: string;
  sacHsnCode: string;
  taxTreatment: ChargeTaxTreatment;
  effectiveFrom: string;
  effectiveTo: string;
  remarks: string;
}

const EMPTY_FORM: ChargeTaxMappingForm = {
  chargeType: '',
  itemId: '',
  itemName: '',
  rateCode: '',
  sacHsnCode: '',
  taxTreatment: 'TAXABLE',
  effectiveFrom: '',
  effectiveTo: '',
  remarks: '',
};

export { todayIso };

export const dateOnly = (v?: string | null): string => (v ? v.slice(0, 10) : '');

export const treatmentLabel = (v?: string | null): string =>
  TAX_TREATMENT_OPTIONS.find((o) => o.value === v)?.label ?? (v || '');

/**
 * "All room categories" / "All meal plans" / "All add-ons" for a general rule; for
 * any other charge type (which has no items) "All <name> charges".
 */
export const appliesToAll = (v?: string | null, typeName?: string): string => {
  if (v === 'ROOM') return 'All room categories';
  if (v === 'MEAL') return 'All meal plans';
  if (v === 'ADDON') return 'All add-ons';
  return typeName ? `All ${typeName.toLowerCase()} charges` : 'All items';
};

interface Props {
  isOpen: boolean;
  mode: FormModalMode;
  record: ChargeTaxMapping | null;
  /** Prefill for a new rule (e.g. the charge type from the coverage notice). */
  preset?: Partial<ChargeTaxMappingForm>;
  propertyId: string;
  propertyLabel: string;
  jurisdictionCode: string;
  /** Every rule of the property, history included — for the outcome wording. */
  rules: ChargeTaxMapping[];
  /** Tax master code → "GST 18%". */
  rateLabel: (code: string) => string;
  /** Active charge types from the Charge Types master, for the picker. */
  chargeTypeOptions: Array<{ value: string; label: string }>;
  /** Charge type code → its name. */
  chargeTypeName: (code?: string | null) => string;
  taxEnabled: boolean;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

const ReadOnlyField: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <input type="text" className="form-input" value={value || '—'} title={value || undefined} readOnly style={{ background: 'var(--bg-secondary)' }} />
  </div>
);

const itemLabel = (t: ChargeType | '') => (t === 'ROOM' ? 'Room Category' : t === 'MEAL' ? 'Meal Plan' : t === 'ADDON' ? 'Add-on' : 'Item');

const ChargeTaxMappingFormModal: React.FC<Props> = ({
  isOpen, mode, record, preset, propertyId, propertyLabel, jurisdictionCode, rules, rateLabel, taxEnabled,
  chargeTypeOptions, chargeTypeName,
  tabId, moduleName, createInstanceId, onClose, onSaved, onRestoreModal,
}) => {
  const chargeTypeLabel = chargeTypeName;
  // Labels of the last loaded item options, so the chosen item's name can be stated.
  const itemNames = useRef<Map<string, string>>(new Map());

  const populateOnOpen = useCallback((m: FormModalMode, r: ChargeTaxMapping | null): ChargeTaxMappingForm => {
    if (!r || m === 'create') return { ...EMPTY_FORM, effectiveFrom: todayIso(), ...preset };
    return {
      chargeType: r.chargeType,
      itemId: r.itemId ?? '',
      itemName: r.itemName ?? '',
      rateCode: r.rateCode,
      sacHsnCode: r.sacHsnCode ?? '',
      taxTreatment: r.taxTreatment,
      effectiveFrom: m === 'copy' ? '' : dateOnly(r.effectiveFrom),
      effectiveTo: '',
      remarks: '',
    };
  }, [preset]);

  const validate = useCallback((f: ChargeTaxMappingForm, m: FormModalMode): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (m === 'edit') {
      if (!f.effectiveTo) errors.effectiveTo = 'Choose the date from which the rule is no longer used.';
      else if (record && f.effectiveTo <= dateOnly(record.effectiveFrom)) {
        errors.effectiveTo = `Must be after ${formatDay(record.effectiveFrom)}.`;
      }
      return errors;
    }
    if (!f.chargeType) errors.chargeType = 'Charge is required.';
    if (!f.rateCode) errors.rateCode = 'Tax master is required.';
    if (!f.effectiveFrom) errors.effectiveFrom = 'Choose the date the rule takes effect.';
    if (m === 'copy' && record && f.effectiveFrom && f.effectiveFrom <= dateOnly(record.effectiveFrom)) {
      errors.effectiveFrom = `Must be after ${formatDay(record.effectiveFrom)}, when the current rule started.`;
    }
    return errors;
  }, [record]);

  const consequence = useCallback((f: ChargeTaxMappingForm, m: FormModalMode) => {
    if (m === 'edit') return record ? stopRuleConsequence(record, f.effectiveTo, rules, rateLabel, taxEnabled) : null;
    return newRuleConsequence(f, rules, rateLabel, todayIso());
  }, [record, rules, rateLabel, taxEnabled]);

  const onSave = useCallback(async (f: ChargeTaxMappingForm, m: FormModalMode, r: ChargeTaxMapping | null) => {
    if (m === 'edit') {
      if (!r) return;
      await chargeTaxMappingService.close(r.id, {
        effectiveTo: f.effectiveTo,
        rowVersion: r.rowVersion,
        remarks: f.remarks.trim() || null,
      });
      return;
    }
    await chargeTaxMappingService.create({
      propertyId,
      chargeType: f.chargeType as ChargeType,
      itemId: f.itemId || null,
      jurisdictionCode,
      rateCode: f.rateCode,
      sacHsnCode: f.sacHsnCode.trim() || null,
      taxTreatment: f.taxTreatment,
      effectiveFrom: f.effectiveFrom,
      remarks: f.remarks.trim() || null,
    });
  }, [propertyId, jurisdictionCode]);

  const loadTaxMasters = useCallback(async (input: string): Promise<SelectOption[]> => {
    const page = await taxMasterService.list(jurisdictionCode, { searchTerm: input || undefined, page: 1, pageSize: 50 });
    return page.items.map((t) => ({ value: t.rateCode, label: `${t.rateCode} — ${t.name} (${t.rateSummary})` }));
  }, [jurisdictionCode]);

  const loadItems = useCallback(async (chargeType: ChargeType | '', input: string): Promise<SelectOption[]> => {
    const params = { propertyId, searchTerm: input || undefined, page: 1, pageSize: 100, includeInactive: false };
    let options: SelectOption[] = [];
    let names: Array<[string, string]> = [];
    if (chargeType === 'ROOM') {
      const res = await roomCategoryService.getAll(params);
      options = (res.data ?? []).map((c) => ({ value: c.id, label: `${c.categoryCode} - ${c.categoryName}` }));
      names = (res.data ?? []).map((c) => [c.id, c.categoryName]);
    } else if (chargeType === 'MEAL') {
      const res = await mealPlanService.getAll(params);
      options = (res.data ?? []).map((p) => ({ value: p.id, label: `${p.planCode} - ${p.planName}` }));
      names = (res.data ?? []).map((p) => [p.id, p.planName]);
    } else if (chargeType === 'ADDON') {
      const res = await addonService.getAll(params);
      options = (res.data ?? []).map((a) => ({ value: a.id, label: `${a.addonCode} - ${a.addonName}` }));
      names = (res.data ?? []).map((a) => [a.id, a.addonName]);
    }
    names.forEach(([id, name]) => itemNames.current.set(id, name));
    return options;
  }, [propertyId]);

  return (
    <EnterpriseFormModal<ChargeTaxMappingForm, ChargeTaxMapping>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun="Tax Rule"
      copyVerb="Replace"
      editVerb="Stop"
      emptyForm={EMPTY_FORM}
      populateOnOpen={populateOnOpen}
      validate={validate}
      onSave={onSave}
      consequence={consequence}
      confirmSaveTitle={(m) => (m === 'edit' ? 'Stop Tax Rule?' : m === 'copy' ? 'Replace Tax Rule?' : 'Add Tax Rule?')}
      confirmSaveLabel={(m) => (m === 'edit' ? 'Stop Rule' : m === 'copy' ? 'Replace' : 'Add')}
      successMessage={(m) => (m === 'edit' ? 'Tax rule stopped.' : m === 'copy' ? 'Tax rule replaced.' : 'Tax rule added.')}
      size="md"
      tabId={tabId}
      moduleName={moduleName}
      createInstanceId={createInstanceId}
      recordKey={record?.id}
      getStripFormLabel={(f) => (f.chargeType ? chargeTypeLabel(f.chargeType) : undefined)}
      getStripRecordLabel={(r) => (r ? `${chargeTypeLabel(r.chargeType)} ${r.itemName ?? ''}`.trim() : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {({ form, setForm, mode: m, record: r, getFieldError, clearFieldError, isSaving }) => {
        const set = <K extends keyof ChargeTaxMappingForm>(key: K, value: ChargeTaxMappingForm[K]) => {
          clearFieldError(key as string);
          setForm((prev) => ({ ...prev, [key]: value }));
        };

        if (m === 'edit' && r) {
          return (
            <>
              <div className="form-grid-2">
                <ReadOnlyField label="Charge" value={chargeTypeLabel(r.chargeType)} />
                <ReadOnlyField label="Applies to" value={r.itemName ?? appliesToAll(r.chargeType, chargeTypeLabel(r.chargeType))} />
                <ReadOnlyField label="Tax master" value={`${r.rateCode} — ${rateLabel(r.rateCode)}`} />
                <ReadOnlyField label="Valid from" value={formatDay(r.effectiveFrom)} />
                <div className="form-group" data-field="effectiveTo">
                  <label className="form-label">No longer used from <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-input${getFieldError('effectiveTo') ? ' form-field-error' : ''}`}
                    value={form.effectiveTo}
                    min={dateOnly(r.effectiveFrom)}
                    onChange={(e) => set('effectiveTo', e.target.value)}
                    disabled={isSaving}
                  />
                  {getFieldError('effectiveTo')
                    ? <div className="form-error-message">{getFieldError('effectiveTo')}</div>
                    : <FieldHint>The last check-in date this rule covers is the day before.</FieldHint>}
                </div>
                <div className="form-group" data-field="remarks">
                  <label className="form-label">Remarks</label>
                  <input
                    type="text"
                    className="form-input"
                    maxLength={500}
                    value={form.remarks}
                    onChange={(e) => set('remarks', e.target.value)}
                    placeholder="Why the rule is stopped"
                    disabled={isSaving}
                  />
                </div>
              </div>
            </>
          );
        }

        const isReplace = m === 'copy';
        const itemSeed: SelectOption[] | undefined = form.itemId && form.itemName ? [{ value: form.itemId, label: form.itemName }] : undefined;
        const rateSeed: SelectOption[] | undefined = form.rateCode ? [{ value: form.rateCode, label: `${form.rateCode} — ${rateLabel(form.rateCode)}` }] : undefined;

        return (
          <>
            <div className="form-grid-2">
              <ReadOnlyField label="Property" value={propertyLabel} />
              <div className="form-group" data-field="chargeType">
                <SyutSelect
                  label={<>Charge <span className="text-danger">*</span></>}
                  options={form.chargeType && !chargeTypeOptions.some((o) => o.value === form.chargeType)
                    ? [{ value: form.chargeType, label: chargeTypeLabel(form.chargeType) }, ...chargeTypeOptions]
                    : chargeTypeOptions}
                  value={form.chargeType}
                  onChange={(v) => {
                    clearFieldError('chargeType');
                    setForm((prev) => ({ ...prev, chargeType: ((v as string) || '') as ChargeType | '', itemId: '', itemName: '' }));
                  }}
                  placeholder="Select a charge type..."
                  isDisabled={isReplace || isSaving}
                  menuPortalTarget={document.body}
                  error={getFieldError('chargeType')}
                />
              </div>
              <div className="form-group" data-field="itemId">
                <SyutSelect
                  key={`item-${form.chargeType}-${propertyId}`}
                  label={`Applies to (${itemLabel(form.chargeType)})`}
                  isAsync
                  loadOptions={(input) => loadItems(form.chargeType, input)}
                  defaultOptions
                  cacheOptions={false}
                  seedOptions={itemSeed}
                  value={form.itemId}
                  onChange={(v) => {
                    const id = (v as string) || '';
                    setForm((prev) => ({ ...prev, itemId: id, itemName: id ? (itemNames.current.get(id) ?? '') : '' }));
                  }}
                  placeholder={form.chargeType ? appliesToAll(form.chargeType, chargeTypeLabel(form.chargeType)) : 'Choose a charge first'}
                  isClearable
                  isDisabled={!isItemChargeType(form.chargeType) || isReplace || isSaving}
                  menuPortalTarget={document.body}
                  helperText={!form.chargeType || isItemChargeType(form.chargeType)
                    ? 'Leave empty to cover every item of this type.'
                    : 'This charge type has no items: the rule covers all of its charges.'}
                />
              </div>
              <div className="form-group" data-field="rateCode">
                <SyutSelect
                  label={<>{isReplace ? 'New tax master' : 'Tax master'} <span className="text-danger">*</span></>}
                  isAsync
                  loadOptions={(input) => loadTaxMasters(input)}
                  defaultOptions
                  cacheOptions={false}
                  seedOptions={rateSeed}
                  value={form.rateCode}
                  onChange={(v) => set('rateCode', (v as string) || '')}
                  placeholder="Select tax master..."
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  error={getFieldError('rateCode')}
                  helperText={getFieldError('rateCode') ? undefined : 'Decides the rate. Maintained on the Tax Masters screen.'}
                />
              </div>
              <div className="form-group" data-field="sacHsnCode">
                <label className="form-label">SAC / HSN</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.sacHsnCode}
                  maxLength={10}
                  placeholder="e.g., 996311"
                  onChange={(e) => set('sacHsnCode', e.target.value.replace(/\s/g, ''))}
                  disabled={isSaving}
                />
                <FieldHint>Printed on tax documents. Does not change the rate.</FieldHint>
              </div>
              <div className="form-group" data-field="taxTreatment">
                <SyutSelect
                  label={<>Tax status <span className="text-danger">*</span></>}
                  options={TAX_TREATMENT_OPTIONS}
                  value={form.taxTreatment}
                  onChange={(v) => set('taxTreatment', ((v as string) || 'TAXABLE') as ChargeTaxTreatment)}
                  isDisabled={isSaving}
                  menuPortalTarget={document.body}
                  helperText="Exempt and Zero-rated charges carry no tax."
                />
              </div>
              <div className="form-group" data-field="effectiveFrom">
                <label className="form-label">Takes effect from <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                  value={form.effectiveFrom}
                  min={isReplace && r ? dateOnly(r.effectiveFrom) : undefined}
                  onChange={(e) => set('effectiveFrom', e.target.value)}
                  disabled={isSaving}
                />
                {getFieldError('effectiveFrom')
                  ? <div className="form-error-message">{getFieldError('effectiveFrom')}</div>
                  : <FieldHint>Compared with the booking&apos;s check-in date.</FieldHint>}
              </div>
              <div className="form-group" data-field="remarks">
                <label className="form-label">Remarks</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={500}
                  value={form.remarks}
                  onChange={(e) => set('remarks', e.target.value)}
                  placeholder="e.g., GST notification reference"
                  disabled={isSaving}
                />
              </div>
            </div>
          </>
        );
      }}
    </EnterpriseFormModal>
  );
};

export default ChargeTaxMappingFormModal;
